import React, { useState } from 'react';
import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  TextField,
  Select,
} from '@material-ui/core';
import { ResourceDto } from '../resources.dtos';
import {
  FormikErrors,
  FormikHandlers,
  FormikHelpers,
  FormikTouched,
} from 'formik';
import { DocumentTypesEnum_class } from '../../../../../../common/enums/documentTypes';
import AttachmentDropzone from '../../../../../../common/components/dialog/AttachmentDropzone';

interface IProps {
  values: ResourceDto;
  errors: FormikErrors<ResourceDto>;
  touched: FormikTouched<ResourceDto>;
  handleChange: FormikHandlers['handleChange'];
  handleBlur: FormikHandlers['handleBlur'];
  setFieldValue: FormikHelpers<ResourceDto>['setFieldValue'];
}

const ResourceCreateForm: React.FC<IProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
}) => {
  const [fileUploads, setFileUploads] = useState<File[]>([]);

  const onAttachmentDelete = (file: File) => {
    setFileUploads(
      fileUploads.filter((attachment) => attachment.name !== file.name)
    );

    setFieldValue(
      'fileUpload',
      fileUploads.filter((attachment) => attachment.name !== file.name)
    );
  };

  const onAttachmentUpload = (files: File[]) => {
    setFileUploads([...fileUploads, ...files]);
    setFieldValue('fileUpload', [...fileUploads, ...files]);
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <TextField
          size='small'
          margin='none'
          value={values.name || ''}
          label='File Name'
          name='name'
          onChange={handleChange}
          onBlur={(e) => {
            handleBlur(e);
          }}
          error={Boolean(errors.name && touched.name)}
          helperText={Boolean(errors.name) && touched.name && errors.name}
          fullWidth
          required
        />
        <FormControl fullWidth required>
          <InputLabel>Document Type</InputLabel>
          <Select
            name='documentTypeId'
            value={values.documentTypeId}
            onChange={(e: any) => {
              handleChange(e);
              if (e.target.value === DocumentTypesEnum_class.enum.URL) {
                setFieldValue('isUrl', true);
                setFieldValue('isFile', false);
              } else if (
                e.target.value === DocumentTypesEnum_class.enum.PDF ||
                e.target.value === DocumentTypesEnum_class.enum.Image
              ) {
                setFieldValue('isUrl', false);
                setFieldValue('isFile', true);
              }
            }}
            onBlur={handleBlur}
            error={Boolean(errors.documentTypeId)}
          >
            <MenuItem value={0} disabled>
              Select Document Type
            </MenuItem>
            {Object.keys(DocumentTypesEnum_class.enumByNumber)
              .filter((f) => isNaN(+f) === false)
              .map((o, i) => (
                <MenuItem key={o.toString() + i} value={+o}>
                  {DocumentTypesEnum_class.enumByNumber[+o]
                    .match(/[A-Za-z]+/g)
                    ?.map((m) => `${m} `)}
                </MenuItem>
              ))}
          </Select>
          <FormHelperText error>{errors.documentTypeId}</FormHelperText>
        </FormControl>
        {values.isUrl ? (
          <TextField
            size='small'
            margin='none'
            value={values.fileUrl || ''}
            label='URL'
            name='fileUrl'
            onChange={handleChange}
            onBlur={(e) => {
              handleBlur(e);
            }}
            error={Boolean(errors.fileUrl && touched.fileUrl)}
            helperText={
              Boolean(errors.fileUrl) && touched.fileUrl && errors.fileUrl
            }
            fullWidth
            required
          />
        ) : null}
        {values.isFile ? (
          <>
            <InputLabel>Image/PDF Upload</InputLabel>
            <AttachmentDropzone
              attachments={fileUploads}
              onDelete={onAttachmentDelete}
              onDrop={onAttachmentUpload}
              maxFiles={1}
            />
            <FormHelperText error>{errors.fileUpload}</FormHelperText>
          </>
        ) : null}
      </Grid>
    </Grid>
  );
};

export default ResourceCreateForm;
